import { Link, useNavigate } from "react-router";
import { ArrowRight, CalendarCheck, Lock, ShieldCheck, UserRound } from "lucide-react";
import { useEffect } from "react";

import { Logo } from "@/components/common/logo";
import { FadeUp, LiftCard, StaggerGroup } from "@/components/common/motion";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { usePageMeta } from "@/lib/page-meta";

export function WelcomeRoute() {
  usePageMeta({
    title: "HealNova Doctor Portal — Welcome",
    description:
      "Login or signup as a HealNova clinician to manage your profile, availability and patient appointments.",
  });
  return <WelcomePage />;
}

const HIGHLIGHTS = [
  {
    icon: UserRound,
    title: "One practice profile",
    body: "Manage qualifications, languages, fees and consultation modes in one place.",
  },
  {
    icon: CalendarCheck,
    title: "Appointments at a glance",
    body: "See today's, upcoming and completed consultations with patient context.",
  },
  {
    icon: ShieldCheck,
    title: "Verified clinician access",
    body: "Every account is reviewed by the HealNova team before it becomes visible to patients.",
  },
  {
    icon: Lock,
    title: "Secure & private",
    body: "We follow strict data protection standards to keep your practice information safe.",
  },
];

function WelcomePage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // A returning, already-signed-in clinician shouldn't land on the welcome gate.
  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [loading, user, navigate]);

  return (
    <div className="min-h-screen bg-background">
      <header className="relative z-20 mx-auto flex w-full max-w-6xl items-center justify-between px-5 py-6 sm:px-8">
        <Logo />
        <Button asChild className="h-11 rounded-xl px-6">
          <Link to="/login">Login to Portal</Link>
        </Button>
      </header>

      <main>
        <Hero />
        <Highlights />
      </main>

      <SiteFooter />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="aurora-soft" aria-hidden="true" />

      {/* The portrait fades out over its last sixth, so the section's own bottom
          padding stays tight — the fade supplies the breathing room. */}
      <div className="relative z-10 mx-auto grid w-full max-w-6xl items-center gap-10 px-5 pt-8 pb-10 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:pt-12 lg:pb-4">
        <div>
          <FadeUp duration={0.6}>
            <p className="text-xs font-semibold tracking-[0.22em] text-primary uppercase">
              Clinician workspace
            </p>
          </FadeUp>

          <FadeUp delay={0.08} duration={0.65}>
            <h1 className="mt-5 font-display text-4xl leading-[1.08] font-semibold tracking-tight text-balance sm:text-5xl lg:text-[3.65rem]">
              Care, coordinated around <span className="text-primary">every patient.</span>
            </h1>
          </FadeUp>

          <FadeUp delay={0.16}>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-muted-foreground sm:text-[1.06rem]">
              HealNova brings consultations, records and follow-ups into one calm workspace — so
              your attention stays on the person in front of you.
            </p>
          </FadeUp>

          <FadeUp delay={0.24} className="mt-9 flex flex-col gap-3 sm:flex-row">
            <Button asChild size="lg" className="group h-13 rounded-xl px-7 text-base">
              <Link to="/login">
                Login to your account
                <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="h-13 rounded-xl px-7 text-base transition-colors hover:border-primary/30 hover:bg-secondary"
            >
              <Link to="/signup">Create new account</Link>
            </Button>
          </FadeUp>
        </div>

        <FadeUp delay={0.18} duration={0.8} distance={32} className="relative hidden lg:block">
          <div className="hero-portrait relative mx-auto aspect-[466/628] w-full max-w-md">
            <img
              src="/hero-clinician.png"
              alt="A clinician reviewing patient notes on a tablet"
              width={466}
              height={628}
              className="size-full object-cover"
            />
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

function Highlights() {
  return (
    <section className="relative pb-20 sm:pb-24">
      <div className="mx-auto w-full max-w-6xl px-5 sm:px-8">
        <FadeUp inView className="text-center">
          <h2 className="font-display text-3xl font-semibold tracking-tight text-balance sm:text-[2.1rem]">
            Everything you need in one place
          </h2>
          <span
            className="mx-auto mt-5 block h-0.5 w-16 rounded-full bg-primary"
            aria-hidden="true"
          />
        </FadeUp>

        <StaggerGroup
          step={0.1}
          delay={0.1}
          className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4"
        >
          {HIGHLIGHTS.map(({ icon: Icon, title, body }) => (
            <LiftCard key={title} className="p-6 sm:p-7">
              <span className="grid size-14 place-items-center rounded-full bg-secondary text-primary">
                <Icon className="size-6" />
              </span>
              <h3 className="mt-6 font-display text-lg font-semibold">{title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{body}</p>
            </LiftCard>
          ))}
        </StaggerGroup>
      </div>
    </section>
  );
}

function SiteFooter() {
  return (
    <footer className="bg-primary text-primary-foreground py-8">
      <div className="mx-auto w-full max-w-6xl px-5 sm:px-8">
        <div className="flex w-full items-center justify-center">
          <p className="text-sm text-primary-foreground/70">
            © 2026 HealNova Healthcare | All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
